import React, { useRef, useMemo, useLayoutEffect, useState } from 'react';
import { FeatureFlags } from 'V2/Utilities/FeatureFlags';

import { ProgressIndicator } from './ProgressIndicator';
import { ProgressLabel } from './ProgressLabel';
import {
  getPhraseForStep,
  calculateLabelLayout,
  MOBILE_MAX_WIDTH_PX,
  LABEL_ANCHOR_OFFSET_PX,
  STEPS_WITHOUT_PROGRESS_BAR,
} from './utils';
import type { QuoteMagicProgressBarProps } from './types';
import type { LabelLayout } from './utils';
import styles from './styles.module.css';

const DEFAULT_LABEL_WIDTH_PX = 180;
const LABEL_HORIZONTAL_PADDING_PX = 40;

export const QuoteMagicProgressBar: React.FC<QuoteMagicProgressBarProps> = ({
  percentage,
  stepName,
}) => {
  const normalizedPercentage = Math.max(0, Math.min(100, percentage));
  const showProgressBar = !STEPS_WITHOUT_PROGRESS_BAR.has(stepName);
  const trackRef = useRef<HTMLDivElement>(null);
  const labelTextContentRef = useRef<HTMLSpanElement>(null);
  const [labelLayout, setLabelLayout] = useState<LabelLayout>({
    position: 'right',
    shouldTruncate: false,
    renderWidthPx: DEFAULT_LABEL_WIDTH_PX,
  });

  const label = useMemo(
    () => (showProgressBar ? getPhraseForStep(stepName) : null),
    [showProgressBar, stepName]
  );

  // Use useLayoutEffect to prevent visual flashing before layout calculations
  useLayoutEffect(() => {
    if (!label) return;

    const trackElement = trackRef.current;
    const labelTextContentElement = labelTextContentRef.current;
    let animationFrameId: number | null = null;

    if (!trackElement || !labelTextContentElement) return;

    const updateLabelLayout = () => {
      const trackRect = trackElement.getBoundingClientRect();
      const wizardPositionPx = (normalizedPercentage / 100) * trackRect.width;
      const labelNaturalWidthPx =
        labelTextContentElement.scrollWidth + LABEL_HORIZONTAL_PADDING_PX;
      const viewportWidthPx = window.innerWidth;

      const nextLayout = calculateLabelLayout({
        trackLeftPx: trackRect.left,
        wizardPositionPx,
        labelWidthPx: labelNaturalWidthPx,
        viewportWidthPx,
        isMobile: viewportWidthPx <= MOBILE_MAX_WIDTH_PX,
      });

      setLabelLayout((currentLayout) => {
        if (
          currentLayout.position === nextLayout.position &&
          currentLayout.shouldTruncate === nextLayout.shouldTruncate &&
          currentLayout.maxLabelWidthPx === nextLayout.maxLabelWidthPx &&
          currentLayout.renderWidthPx === nextLayout.renderWidthPx
        ) {
          return currentLayout;
        }

        return nextLayout;
      });
    };

    const scheduleLabelLayoutUpdate = () => {
      if (animationFrameId !== null) return;

      if (typeof requestAnimationFrame !== 'undefined') {
        animationFrameId = requestAnimationFrame(() => {
          animationFrameId = null;
          updateLabelLayout();
        });

        return;
      }

      updateLabelLayout();
    };

    updateLabelLayout();

    window.addEventListener('resize', scheduleLabelLayoutUpdate);

    let resizeObserver: ResizeObserver | undefined;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleLabelLayoutUpdate);
      resizeObserver.observe(trackElement);
      resizeObserver.observe(labelTextContentElement);
    }

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleLabelLayoutUpdate);

      if (
        animationFrameId !== null &&
        typeof cancelAnimationFrame !== 'undefined'
      ) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [label, normalizedPercentage]);

  if (
    !showProgressBar ||
    !FeatureFlags.canView('display_quote_magic_progress_bar')
  ) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.progressTrack}
        ref={trackRef}
        role="progressbar"
        aria-label="Quote completion progress"
        aria-valuenow={normalizedPercentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`${normalizedPercentage}% complete.`}
        style={
          {
            '--wizard-position': `${normalizedPercentage}%`,
            '--progress-label-anchor-offset': `${LABEL_ANCHOR_OFFSET_PX}px`,
          } as React.CSSProperties
        }
      >
        <div
          className={styles.progressFill}
          style={{
            width: `${normalizedPercentage}%`,
          }}
          data-testid="quoteMagic-progress-bar"
        >
          {normalizedPercentage > 0 && (
            <span className={styles.percentageText} aria-hidden="true">
              {normalizedPercentage}%
            </span>
          )}
        </div>
        <ProgressIndicator percentage={normalizedPercentage} />
        {label && (
          <ProgressLabel
            label={label}
            position={labelLayout.position}
            renderWidthPx={labelLayout.renderWidthPx}
            shouldTruncate={labelLayout.shouldTruncate}
            maxWidthPx={labelLayout.maxLabelWidthPx}
            labelTextContentRef={labelTextContentRef}
          />
        )}
      </div>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={styles.srOnly}
      >
        {label
          ? `${normalizedPercentage}% complete. ${label}`
          : `${normalizedPercentage}% complete.`}
      </div>
    </div>
  );
};
